// See EnrollmentTray.spec.md for ENT-FR-001 through ENT-FR-004
import type { ReactNode } from 'react'
import { useState } from 'react'
import { canEnrollHosts } from 'app/plugins/infrastructure/components/common/service-health/storage-connectivity/helpers'
import TaskTray, { type TaskTrayItem } from 'core/components/task-tray/TaskTray'
import EnrollHostsDrawer from './EnrollHostsDrawer'
import { EnrollmentRunnerProvider, useEnrollmentRunnerContext } from './EnrollmentRunnerContext'
import { isRunActive } from './enrollmentRuns'
import { trayItem } from './enrollmentSelectors'

// ENT-FR-001: the runner and the tray only mount when the enroll_hosts flag is on (default on)
// and the user can run the storage check.
// Nothing in here (no polling, no verify calls) runs otherwise (SC-002).
export const EnrollmentFeature = ({ children }: { children: ReactNode }) => {
  if (!canEnrollHosts()) return <>{children}</>

  return (
    <EnrollmentRunnerProvider>
      {children}
      <EnrollmentTray />
    </EnrollmentRunnerProvider>
  )
}

// ENT-FR-002..004: one card per run of the current region, newest first (the context already
// sorts them). Show details opens the read only drawer; X dismisses every ended run.
export const EnrollmentTray = () => {
  const { runs, dismissRuns, drawerOpen } = useEnrollmentRunnerContext()
  const [openRunId, setOpenRunId] = useState<string | null>(null)
  // ENT-FR-006: ids of the active runs the user hid with X. A new run changes the key, so the
  // tray shows again. Hiding never stops the checks.
  const [hiddenKey, setHiddenKey] = useState<string | null>(null)
  const activeKey = runs
    .filter(isRunActive)
    .map((run) => run.id)
    .join()

  const items: TaskTrayItem[] = runs.map((run) => {
    const { id, status, message } = trayItem(run)
    const label = (run.hosts ?? []).map((host) => host.hostName).join(', ')
    return { id, status, message, label, actionLabel: 'Show details' }
  })

  return (
    <>
      {/* ENT-FR-005: hidden while any Enroll Hosts drawer is open */}
      {!drawerOpen && !(activeKey && activeKey === hiddenKey) && (
        <TaskTray
          title="Hosts Enrollment Checks"
          items={items}
          onAction={(item) => setOpenRunId(item.id)}
          onDismiss={() => {
            dismissRuns(runs.map((run) => run.id))
            setHiddenKey(activeKey)
          }}
        />
      )}
      {openRunId && (
        <EnrollHostsDrawer hosts={[]} runId={openRunId} onClose={() => setOpenRunId(null)} />
      )}
    </>
  )
}

export default EnrollmentFeature
